import { rem } from 'polished'
import { memo, useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import TextButton from 'LuxKit/Button/TextButton'
import { SVG_ICON_SIZES } from 'LuxKit/SvgIcon'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ModalContext from 'contexts/ModalContext'
import { take } from 'lib/array/arrayUtils'
import getPropertyIcon from 'lib/offer/getPropertyIcon'

import PropertyFacilitiesModal from './PropertyFacilitiesModal'

const MAX_POPULAR_FACILITIES = 6

const Facilities = styled.ul`
  display: grid;
  gap: ${rem(8)} ${rem(16)};
  margin: 0;

  // Two columns on mobile, possibly more on tablet and desktop.
  grid-template-columns: repeat(2, 1fr);
  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(auto-fill, minmax(${rem(180)}, 1fr));
  }
`

const Facility = styled.li`
  display: flex;
  gap: ${rem(8)};
  margin: 0;

  // Align icon with first line of text (in case it wraps).
  align-items: start;
  line-height: ${SVG_ICON_SIZES.S};
`

interface Props {
  popularFacilities: Array<App.Property.AmenityItem>
  facilities: string | undefined
  amenities: Array<App.Property.AmenityGroup>
}

function PropertyPopularFacilities({ popularFacilities, facilities, amenities }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  // TODO(CRO-6629): Consider supporting LAB-172 and boosting whatever matches active amenity
  // filters to the start of the list.
  const displayPopularFacilities = useMemo(() => take(popularFacilities, MAX_POPULAR_FACILITIES).map(({ id, name, iconName }) => ({
    key: id,
    name,
    Icon: getPropertyIcon(iconName),
  })), [popularFacilities])

  const popularFacilitiesTitle = intl.formatMessage({
    id: 'c7LMGlZTj3hS1J8Sz12a',
    defaultMessage: 'Popular facilities',
    description: 'Heading for popular facilities section on property offer page',
  })
  const viewAllFacilitiesText = intl.formatMessage({
    id: 'Rjk27OnzlLbyLEtLQTLq',
    defaultMessage: 'View all facilities',
    description: 'Button text to view all facilities on property offer page',
  })

  const facilitiesModalProps = useMemo(() => {
    if (facilities) return { facilities }
    if (amenities.length) return { amenities }
  }, [facilities, amenities])
  const canShowFacilitiesModal = !!facilitiesModalProps
  const showFacilitiesModal = useCallback(() => {
    if (!facilitiesModalProps) return
    showModal(<PropertyFacilitiesModal {...facilitiesModalProps} />)
  }, [facilitiesModalProps, showModal])

  return <Group direction="vertical" gap={8}>
    {/* TODO(HP-1614): Unify heading variants. Descriptions proxied from bedbank service tend to use
      * level 4 headings, but `<OfferPromotions/>` (which admittedly is pretty rare) uses a level 3
      * heading.
      */}
    <Heading variant="heading4" as="h4">{popularFacilitiesTitle}</Heading>
    <Facilities>
      {displayPopularFacilities.map(({ key, name, Icon }) => <Facility key={key}>
        <Icon size="S" />{name}
      </Facility>)}
    </Facilities>
    {/* NB: The `<div/>` wrapper is a hack to prevent the button from taking up the container's full
      * width.
      */}
    {canShowFacilitiesModal && <div>
      <TextButton horizontalOutdent="start" kind="tertiary" onClick={showFacilitiesModal}>{viewAllFacilitiesText}</TextButton>
    </div>}
  </Group>
}

export default memo(PropertyPopularFacilities)
